import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { RECURRENCES } from "@/lib/format";
import { LEAD_OPTIONS } from "@/lib/push";
import { playTap } from "@/lib/sounds";

const pad = (n) => String(n).padStart(2, "0");
const localDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

export function ReminderDialog({ open, onOpenChange, memory, reminder, onSaved }) {
  const [form, setForm] = useState({ title: "", date: "", time: "09:00", recurrence: "once", custom_days: 30, memory_id: "", lead: "default", custom_lead: 120 });
  const [memories, setMemories] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const d = reminder?.due_at ? new Date(reminder.due_at) : null;
    const lm = reminder?.lead_minutes;
    const preset = lm != null && [0, 5, 15, 30, 60, 1440].includes(lm);
    setForm({
      title: reminder?.title || (memory ? `Lembrar: ${memory.title}` : ""),
      date: d ? localDate(d) : localDate(new Date(Date.now() + 7 * 864e5)),
      time: d ? localTime(d) : "09:00",
      recurrence: reminder?.recurrence || "once", custom_days: reminder?.custom_days || 30, memory_id: reminder?.memory_id || memory?.id || "",
      lead: lm == null ? "default" : preset ? String(lm) : "custom", custom_lead: lm != null && !preset ? lm : 120,
    });
    if (!memory) api.get("/memories", { params: { limit: 100 } }).then((r) => setMemories(r.data.items));
  }, [open, memory, reminder]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const lead_minutes = form.lead === "default" ? null : form.lead === "custom" ? Number(form.custom_lead) || 0 : Number(form.lead);
      const body = { title: form.title, due_at: new Date(`${form.date}T${form.time}:00`).toISOString(), recurrence: form.recurrence, custom_days: form.recurrence === "custom" ? Number(form.custom_days) : null, memory_id: form.memory_id || null, lead_minutes };
      if (reminder) await api.put(`/reminders/${reminder.id}`, body);
      else await api.post("/reminders", body);
      toast.success(reminder ? "Lembrete atualizado." : "Lembrete criado.");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-3xl" data-testid="reminder-dialog">
        <DialogTitle>{reminder ? "Editar lembrete" : "Novo lembrete"}</DialogTitle>
        <DialogDescription className="sr-only">Defina título, data, horário e recorrência.</DialogDescription>
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-xs ga-muted">Título<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="ga-input mt-1 py-2.5" data-testid="reminder-title-input" /></label>
          {!memory && (
            <label className="block text-xs ga-muted">Memória relacionada (opcional)
              <select value={form.memory_id} onChange={(e) => setForm({ ...form, memory_id: e.target.value })} className="ga-input mt-1 py-2.5" data-testid="reminder-memory-select">
                <option value="">Nenhuma</option>
                {memories.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
              </select>
            </label>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs ga-muted">Data<input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className="ga-input mt-1 py-2.5" data-testid="reminder-date-input" /></label>
            <label className="block text-xs ga-muted">Horário<input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required className="ga-input mt-1 py-2.5" data-testid="reminder-time-input" /></label>
          </div>
          <label className="block text-xs ga-muted">Recorrência
            <select value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })} className="ga-input mt-1 py-2.5" data-testid="reminder-recurrence-select">
              {RECURRENCES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          <label className="block text-xs ga-muted">Avisar
            <select value={form.lead} onChange={(e) => setForm({ ...form, lead: e.target.value })} className="ga-input mt-1 py-2.5" data-testid="reminder-lead-select">
              <option value="default">Antecedência padrão</option>
              {LEAD_OPTIONS.map((o) => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
            </select>
          </label>
          {form.lead === "custom" && <label className="block text-xs ga-muted">Minutos antes<input type="number" min={0} value={form.custom_lead} onChange={(e) => setForm({ ...form, custom_lead: e.target.value })} className="ga-input mt-1 py-2.5" data-testid="reminder-lead-custom-input" /></label>}
          {form.recurrence === "custom" && <label className="block text-xs ga-muted">Repetir a cada (dias)<input type="number" min={1} value={form.custom_days} onChange={(e) => setForm({ ...form, custom_days: e.target.value })} className="ga-input mt-1 py-2.5" data-testid="reminder-custom-days-input" /></label>}
          <button type="submit" onClick={playTap} disabled={busy} className="ga-btn-primary w-full" data-testid="reminder-save-button">{busy ? "Salvando..." : "Salvar lembrete"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
